/**
 * Integrytas Group — Content Management System (CMS)
 * Page: CMS.PAGE.LIST  —  Page Master Management
 *
 * Layout: toolbar + sortable grid → centered modal with four tabs:
 *   Info          — core fields (type, status, parent, sort order, thumbnail)
 *   Translations  — per-language title / slug / description / meta
 *   Elements      — ordered list of content elements (text / image / video)
 *   Security      — csm_role → access_level mapping for this page
 *
 * All writes go through direct apiDb calls (the CMS common-routine RPC
 * will be wired in a future sprint once the server-side function is deployed).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { RichTextEditor } from '../../components/cms/RichTextEditor';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useLanguageOptions } from '../../lib/use_language_options';
import { formatDate, useFormatDate } from '../../lib/format_date';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { hasAccess, getForensicActorId  } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, Plus, RefreshCw } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { useLookupNames } from '../../lib/use_lookup_names';
import type { GridColumnDef } from '../../components/personalization';
import type {
  CmsPageMasterRow,
  CmsPageMasterTlRow,
  CmsPageTypeRow,
  CmsPageStatusRow,
  CmsAssetRow,
  CmsContentElementRow,
  CmsElementTypeRow,
  CmsPageRoleAccessRow,
  CmsAccessLevelRow,
  CmsElementHydrated,
  CmsPageTlForm,
  CmsPageSortKey,
} from '../../types/cms';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyTl(language_id: string): CmsPageTlForm {
  return { language_id, page_title: '', page_description: '', slug: '', meta_title: '', meta_description: '' };
}

// Page-status → badge variant (label comes localized from the lookup _tl).
const STATUS_VARIANT: Record<string, StatusVariant> = {
  published: 'success',
  review:    'warning',
  archived:  'neutral',
  draft:     'info',
};

const PAGE_CODE = 'CMS.PAGE.LIST';
registerUdcHost(PAGE_CODE, 'cms_page_master', 'Pages');

const FILTER_KEY = 'cms.pagelist.filter';

const GRID_COLS: GridColumnDef[] = [
  { key: 'sort_order',    header: 'Sort',       defaultWidth: 60  },
  { key: 'page_title',   header: 'Title',      defaultWidth: 200 },
  { key: 'internal_code', header: 'Code',       defaultWidth: 180 },
  { key: 'slug',          header: 'Slug',       defaultWidth: 160 },
  { key: 'page_type_code', header: 'Type',      defaultWidth: 100 },
  { key: 'status_code',  header: 'Status',     defaultWidth: 100 },
  { key: 'published_at', header: 'Published',  defaultWidth: 110 },
  { key: 'is_active',    header: 'Active',     defaultWidth: 80  },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export interface CmsPageListPageProps { session: AuthSession; }

export function CmsPageListPage({ session }: CmsPageListPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <CmsPageListPageInner session={session} />
    </PersonalizationProvider>
  );
}

function CmsPageListPageInner({ session }: CmsPageListPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]       = useState<CmsPageMasterRow[]>([]);
  const [filter, setFilter]   = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets (Type / Status / Active).
  const [facets, setFacets]   = useState<{ type: string[]; status: string[]; active: string[] }>({ type: [], status: [], active: ['Y'] });
  const [editing, setEditing] = useState<CmsPageMasterRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CmsPageMasterRow | null>(null);
  const sort = useSort<CmsPageSortKey>('cms.pagelist.sort', { key: 'sort_order', dir: 'asc' });
  const udcGrid = useUdcGrid('cms_page_master', PAGE_CODE, { pkColumn: 'page_id', rows: rows as unknown as Record<string, unknown>[] });

  // Lookup data
  const [pageTypes, setPageTypes]     = useState<CmsPageTypeRow[]>([]);
  const [pageStatuses, setPageStatuses] = useState<CmsPageStatusRow[]>([]);

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('cms_page_master')
        .select(`
          page_id, internal_code, page_type_code, status_code,
          parent_page_id, sort_order, thumbnail_asset_id,
          published_at, is_active, created_at, updated_at, modified_by,
          cms_page_master_tl!left(page_title, slug, language_id)
        `)
        .order('sort_order');
      if (e) throw e;
      // Flatten: pull EN_US title as display title
      type TlJoin = { page_title?: string; slug?: string; language_id: string };
      type Row = CmsPageMasterRow & { cms_page_master_tl?: TlJoin[] };
      const flat = ((data ?? []) as Row[]).map(r => {
        const ptls = r.cms_page_master_tl ?? [];
        const tl = ptls.find((t: TlJoin) => t.language_id === activeLocale)   // §8: active locale first
                   ?? ptls.find((t: TlJoin) => t.language_id === 'EN_US') ?? ptls[0];
        return { ...r, page_title: tl?.page_title ?? r.internal_code, slug: tl?.slug ?? '' } as CmsPageMasterRow;
      });
      setRows(flat);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [activeLocale]);

  useEffect(() => {
    void reload();
    void apiDb.from('cms_page_type').select('page_type_code, sort_order, is_active').order('sort_order')
      .then(({ data }) => setPageTypes((data ?? []) as CmsPageTypeRow[]));
    void apiDb.from('cms_page_status').select('status_code, sort_order, is_active').order('sort_order')
      .then(({ data }) => setPageStatuses((data ?? []) as CmsPageStatusRow[]));
  }, [reload]);

  const handleDelete = async (r: CmsPageMasterRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'cms_page_master', 'page_id', r.page_id);
      if (e) throw e;
      await reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR within
    // a facet, AND across facets).
    const base = rows.filter(r => {
      const matchText = !filter.trim() ||
        r.internal_code.toLowerCase().includes(filter.toLowerCase()) ||
        (r.page_title ?? '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.slug ?? '').toLowerCase().includes(filter.toLowerCase());
      const matchType   = facets.type.length === 0   || facets.type.includes(r.page_type_code);
      const matchStatus = facets.status.length === 0 || facets.status.includes(r.status_code);
      const matchActive = facets.active.length === 0 || facets.active.includes(r.is_active);
      return matchText && matchType && matchStatus && matchActive && udcGrid.matchRow(r as unknown as Record<string, unknown>);
    });
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      if (key === 'sort_order') return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      return compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? ''));
    });
  }, [rows, filter, facets, sort, udcGrid]);

  // §17.B1/B4: resolve type/status codes to translated names for display.
  const typeNames   = useLookupNames('cms_page_type',   'cms_page_type_tl',   { codeCol: 'page_type_code', localeCol: 'language_id' });
  const statusNames = useLookupNames('cms_page_status', 'cms_page_status_tl', { codeCol: 'status_code',    localeCol: 'language_id' });
  const typeFacetOptions   = useMemo(() => pageTypes.map(p => ({ value: p.page_type_code, label: typeNames[p.page_type_code] ?? p.page_type_code })), [pageTypes, typeNames]);
  const statusFacetOptions = useMemo(() => pageStatuses.map(s => ({ value: s.status_code, label: statusNames[s.status_code] ?? s.status_code })), [pageStatuses, statusNames]);
  const activeFacetOptions = [
    { value: 'Y', label: t('common.boolean.active', 'Active') },
    { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
  ];
  const anyFilterActive = filter.trim().length > 0 || facets.type.length + facets.status.length + facets.active.length > 0;
  const clearAll = () => { setFilter(''); setFacets({ type: [], status: [], active: [] }); };

  // §17.B13: related-children (expand/contract) for a page row — its sub-pages,
  // resolved from the already-loaded page list (children point back via parent_page_id).
  // Names use the localized page_title; the internal_code rides along in `title` (§17.B4).
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (id: string): RelatedChildGroup[] => [
    {
      key: 'subpages',
      table: 'csm_page_master', pkColumn: 'page_id',
      title: t('cms.pages.related.subpages', 'Sub-pages'),
      items: rows.filter(r => r.parent_page_id === id).map(r => ({
        id: r.page_id, label: r.page_title ?? r.internal_code, title: r.internal_code,
      })),
    },
  ];
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('cms.pages.title', 'Content Pages')}
          description={t('cms.pages.list.description', 'Manage CMS pages, their translations, content elements and access.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('cms.pages.new_page')}
              </button>)}
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('cms.pages.load_failed', "Couldn't load pages.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('cms.pages.search_placeholder', 'Search pages…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('cms.pages.col.page_type_code', 'Type')} options={typeFacetOptions} selected={facets.type} onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('cms.pages.col.status_code', 'Status')} options={statusFacetOptions} selected={facets.status} onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <FacetFilter label={t('cms.pages.col.is_active', 'Active')} options={activeFacetOptions} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('cms.pages.personalize_columns_hint', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          {/* Grid */}
          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? (
          <SkeletonRows rows={8} />
        ) : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-3 py-1.5 text-left" title={t('cms.pages.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <SortableHeader<CmsPageSortKey> key={col.key}
                      sortKey={col.key as CmsPageSortKey} label={col.header}
                      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.page_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.page_id)} hasChildren={hasRelated(r.page_id)} onToggle={() => detail.toggle(r.page_id)} expandLabel={t('cms.pages.related.expand', 'Show related records')} collapseLabel={t('cms.pages.related.collapse', 'Hide related')} tooltip={t('cms.pages.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'sort_order'     && <span className="text-csm-muted">{r.sort_order}</span>}
                        {col.key === 'page_title'     && <span className="font-medium">{r.page_title ?? r.internal_code}</span>}
                        {col.key === 'internal_code'  && <span className="font-mono text-csm-sm text-csm-muted">{r.internal_code}</span>}
                        {col.key === 'slug'           && <span className="font-mono text-csm-sm text-csm-ink-soft">{r.slug ?? '—'}</span>}
                        {col.key === 'page_type_code' && <span className="text-csm-ink-soft">{typeNames[r.page_type_code] ?? r.page_type_code}</span>}
                        {col.key === 'status_code'    && (
                          <StatusBadge
                            variant={STATUS_VARIANT[r.status_code] ?? 'neutral'}
                            label={statusNames[r.status_code] ?? r.status_code}
                          />
                        )}
                        {col.key === 'published_at'   && (
                          <span className="text-csm-sm text-csm-ink-soft">
                            {r.published_at ? formatDate(r.published_at, activeLocale) : <span className="italic text-csm-muted">—</span>}
                          </span>
                        )}
                        {col.key === 'is_active'      && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                        {udcGrid.isUdcKey(col.key)    && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }}
                              className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('common.edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }}
                              className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('common.delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.page_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                      <RelatedChildrenPanel groups={buildRelatedGroups(r.page_id)} session={session} />
                    </td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('cms.pages.no_results')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">+ {t('cms.pages.new_page')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {/* Modal */}
      {(editing || creating) && (
        <CmsPageModal
          session={session}
          page={editing}
          allPages={rows}
          pageTypes={pageTypes}
          pageStatuses={pageStatuses}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('cms.pages.delete.title', 'Delete Page?')}
          message={t('cms.pages.delete.message', 'Delete "{{name}}"? This also removes its elements and security mappings.', { name: deleteTarget.page_title ?? deleteTarget.internal_code })}
          onConfirm={async () => { await handleDelete(deleteTarget); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// EDIT MODAL
// ===========================================================================

type DrawerTab = 'info' | 'translations' | 'elements' | 'security';

interface CmsPageModalProps {
  session: AuthSession;
  page: CmsPageMasterRow | null;
  allPages: CmsPageMasterRow[];
  pageTypes: CmsPageTypeRow[];
  pageStatuses: CmsPageStatusRow[];
  onClose: () => void;
  onSaved: () => void;
}

function CmsPageModal({ session, page, allPages, pageTypes, pageStatuses, onClose, onSaved }: CmsPageModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  // Active languages back the per-language translation rows (no more fixed EN_US/ES_MX pair).
  const { options: languageOptions } = useLanguageOptions();
  const isCreate = page === null;
  const [activeTab, setActiveTab] = useState<DrawerTab>('info');
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const initialValues = {
    internalCode: page?.internal_code ?? '',
    pageType: page?.page_type_code ?? (pageTypes[0]?.page_type_code ?? ''),
    status: page?.status_code ?? 'draft',
    parentId: page?.parent_page_id ?? null as string | null,
    sortOrder: page?.sort_order ?? 0,
    isActive: page ? page.is_active === 'Y' : true,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reset(initialValues); }, [page]);

  // Translations — one row per active language.
  const [translations, setTranslations] = useState<CmsPageTlForm[]>(
    languageOptions.map(o => emptyTl(o.code))
  );

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create mode: the active-language list often isn't loaded yet on first render
  // (useLanguageOptions fetches asynchronously) — fill in the rows once it arrives,
  // without clobbering anything the user may have already typed.
  useEffect(() => {
    if (page) return; // edit mode is seeded by the effect below
    if (languageOptions.length === 0) return;
    setTranslations(prev => (prev.length === 0 ? languageOptions.map(o => emptyTl(o.code)) : prev));
  }, [page, languageOptions]);

  // Load existing translations — ONCE per record. `languageOptions` stays in the deps so
  // we can retry once it resolves (it starts empty on mount / after a fresh login), but the
  // `loadedForPageIdRef` guard means a later languageOptions change (async arrival, or a
  // live UI-language switch mid-edit) never re-fetches and clobbers unsaved translation
  // edits — only a genuine record-identity change does (regression fixed 2026-07-20).
  const loadedForPageIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!page) { loadedForPageIdRef.current = null; return; }
    if (loadedForPageIdRef.current === page.page_id) return; // already loaded for this record
    if (languageOptions.length === 0) return; // wait for the active-language list before building rows
    loadedForPageIdRef.current = page.page_id;
    void apiDb
      .from('cms_page_master_tl')
      .select('*')
      .eq('page_id', page.page_id)
      .then(({ data }) => {
        if (!data) return;
        setTranslations(languageOptions.map(o => {
          const existing = (data as CmsPageMasterTlRow[]).find(t => t.language_id === o.code);
          return existing
            ? { language_id: o.code, page_title: existing.page_title, page_description: existing.page_description ?? '',
                slug: existing.slug, meta_title: existing.meta_title ?? '', meta_description: existing.meta_description ?? '' }
            : emptyTl(o.code);
        }));
      });
  }, [page, languageOptions]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const save = async () => {
    // Eagerly validate so the user gets feedback even without blurring the field.
    if (isCreate) {
      const v = form.internalCode;
      if (!v.trim()) { setInternalCodeError(t('cms.pages.validation.internal_code_required', 'Internal code is required.')); return; }
      if (!/^[A-Z][A-Z0-9_.]*$/.test(v)) {
        setInternalCodeError(t('cms.pages.validation.internal_code_format', 'Must start with a letter and contain only uppercase A–Z, 0–9, dots, or underscores.'));
        return;
      }
      setInternalCodeError(null);
    }
    if (internalCodeError) return;
    setBusy(true); setError(null);
    try {
      if (isCreate) {
        if (!form.internalCode.trim()) throw new Error('Internal code is required.');
        const { row_id, error: e } = await routedInsert(session, 'cms_page_master', {
          internal_code: form.internalCode.trim().toUpperCase(),
          page_type_code: form.pageType, status_code: form.status,
          parent_page_id: form.parentId, sort_order: form.sortOrder,
          is_active: form.isActive ? 'Y' : 'N',
        });
        if (e) throw e;
        const pageId = String(row_id);
        await upsertTranslations(pageId);
      } else if (page) {
        const { error: e } = await routedUpdate(session, 'cms_page_master', 'page_id', page.page_id, {
          page_type_code: form.pageType, status_code: form.status,
          parent_page_id: form.parentId, sort_order: form.sortOrder,
          is_active: form.isActive ? 'Y' : 'N',
          published_at: form.status === 'published' ? new Date().toISOString() : page.published_at,
        });
        if (e) throw e;
        await upsertTranslations(page.page_id);
      }
      reset(form);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const upsertTranslations = async (pageId: string) => {
    // cms_page_master_tl has a composite PK (page_id, language_id) and is not
    // registered with the Common Routine (single-PK only, §2) — keep the direct upsert.
    for (const tl of translations) {
      if (!tl.page_title.trim() && !tl.slug.trim()) continue;
      const { error: e } = await apiDb.from('cms_page_master_tl').upsert({
        page_id: pageId, language_id: tl.language_id,
        page_title: tl.page_title, page_description: tl.page_description || null,
        slug: tl.slug.trim().toLowerCase().replace(/\s+/g, '-'),
        meta_title: tl.meta_title || null, meta_description: tl.meta_description || null,
      }, { onConflict: 'page_id,language_id' });
      if (e) throw e;
    }
  };

  const parentOptions = useMemo(() => [
    { internal_code: '', display_text: t('cms.pages.drawer.fields.no_parent', '(No parent — top level)') },
    ...allPages
      // §17: parent lookup mirrors the list's Active-only default — no inactive parents.
      .filter(p => p.is_active === 'Y' && p.page_id !== page?.page_id)
      .map(p => ({ internal_code: p.page_id, display_text: p.page_title ?? p.internal_code })),
  ], [allPages, page, t]);

  const TABS: { id: DrawerTab; label: string; disabled?: boolean }[] = [
    { id: 'info',         label: t('cms.pages.drawer.tab_info') },
    { id: 'translations', label: t('cms.pages.drawer.tab_translations') },
    { id: 'elements',     label: t('cms.pages.drawer.tab_elements'),  disabled: isCreate },
    { id: 'security',     label: t('cms.pages.drawer.tab_security'),  disabled: isCreate },
  ];

  return (
    <>
      <Modal
        title={isCreate ? t('cms.pages.drawer.new_title') : t('cms.pages.drawer.edit_title')}
        onClose={handleClose}
        width="2xl"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as DrawerTab)}
        headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
            {(activeTab === 'info' || activeTab === 'translations') && (
              <button onClick={() => void save()} disabled={busy}
                      className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
                {busy ? t('common.saving') : t('common.save')}
              </button>
            )}
          </>
        }
      >
        {isPersonalizing && (
          <div className="mb-3 rounded border border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
            {t('cms.pages.personalize_fields_hint', 'Toggle fields — hide or mark required. Click icon to save.')}
          </div>
        )}
        {activeTab === 'info' && (
          <InfoTab
            isCreate={isCreate}
            internalCode={form.internalCode}
            setInternalCode={(v) => setForm(f => ({ ...f, internalCode: v }))}
            internalCodeError={internalCodeError}
            setInternalCodeError={setInternalCodeError}
            pageType={form.pageType}
            setPageType={(v) => setForm(f => ({ ...f, pageType: v }))}
            status={form.status}
            setStatus={(v) => setForm(f => ({ ...f, status: v }))}
            parentId={form.parentId}
            setParentId={(v) => setForm(f => ({ ...f, parentId: v }))}
            sortOrder={form.sortOrder}
            setSortOrder={(v) => setForm(f => ({ ...f, sortOrder: v }))}
            isActive={form.isActive}
            setIsActive={(v) => setForm(f => ({ ...f, isActive: v }))}
            pageTypes={pageTypes} pageStatuses={pageStatuses}
            parentOptions={parentOptions}
            session={session}
            pageId={page?.page_id}
          />
        )}
        {activeTab === 'translations' && (
          <TranslationsTab translations={translations} setTranslations={setTranslations} />
        )}
        {activeTab === 'elements' && page && (
          <ElementsTab session={session} pageId={page.page_id} />
        )}
        {activeTab === 'security' && page && (
          <SecurityTab session={session} pageId={page.page_id} />
        )}
        {error && (
          <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1.5 text-csm-sm text-csm-danger">{error}</div>
        )}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('common.discard_changes.title', 'Discard Changes?')}
          message={t('common.discard_changes.message', 'You have unsaved changes. Discard them and close?')}
          confirmLabel={t('common.discard', 'Discard')}
          onConfirm={() => { setShowDiscard(false); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Info tab
// ---------------------------------------------------------------------------

const INFO_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code',  type: 'text',    label: 'Internal Code', required: true, col_span: 1 },
  { key: 'sort_order',     type: 'number',  label: 'Sort Order',    col_span: 1 },
  { key: 'page_type_code', type: 'select',  label: 'Page Type',     col_span: 1 },
  { key: 'status_code',    type: 'select',  label: 'Status',        col_span: 1 },
  { key: 'parent_page',    type: 'select',  label: 'Parent Page',   col_span: 2 },
  { key: 'is_active',      type: 'boolean', label: 'Active',        col_span: 2 },
];
const INFO_CORE_LAYOUT = toUldCoreLayout(INFO_FIELD_DEFS);

interface InfoTabProps {
  isCreate: boolean;
  internalCode: string; setInternalCode: (v: string) => void;
  internalCodeError: string | null; setInternalCodeError: (v: string | null) => void;
  pageType: string; setPageType: (v: string) => void;
  status: string; setStatus: (v: string) => void;
  parentId: string | null; setParentId: (v: string | null) => void;
  sortOrder: number; setSortOrder: (v: number) => void;
  isActive: boolean; setIsActive: (v: boolean) => void;
  pageTypes: CmsPageTypeRow[];
  pageStatuses: CmsPageStatusRow[];
  parentOptions: { internal_code: string; display_text: string }[];
  session: AuthSession;
  pageId: string | undefined;
}

function InfoTab({ isCreate, internalCode, setInternalCode, internalCodeError, setInternalCodeError,
  pageType, setPageType, status, setStatus,
  parentId, setParentId, sortOrder, setSortOrder, isActive, setIsActive,
  pageTypes, pageStatuses, parentOptions, session, pageId }: InfoTabProps) {
  const { t } = useTranslation();
  // Custom fields placed on this entity (host = the list page, PAGE_CODE) become
  // first-class FieldRenderer items: they interleave with native fields and inherit
  // the same per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('cms_page_master', PAGE_CODE, { pkColumn: 'page_id', pk: pageId });
  const infoFieldDefs = useMemo(() => [...INFO_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const infoCoreLayout = useMemo(() => [...INFO_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const { layout: infoLayout } = useUldLayout('CMS.PAGE.EDIT.INFO.MODAL', infoCoreLayout);
  const typeOptions = pageTypes.map(pt => ({ internal_code: pt.page_type_code, display_text: pt.page_type_code }));
  const statusOptions = pageStatuses.map(s => ({ internal_code: s.status_code, display_text: s.status_code }));
  const CK = 'drawer';

  const validateInternalCode = (v: string) => {
    if (!v.trim()) { setInternalCodeError(t('cms.pages.validation.internal_code_required', 'Internal code is required.')); return; }
    if (!/^[A-Z][A-Z0-9_.]*$/.test(v)) {
      setInternalCodeError(t('cms.pages.validation.internal_code_format', 'Must start with a letter and contain only uppercase A–Z, 0–9, dots, or underscores.'));
      return;
    }
    setInternalCodeError(null);
  };

  return (
    <FieldRenderer
      fields={infoFieldDefs}
      layout={infoLayout}
      columns={2}
      pageCode="CMS.PAGE.EDIT.INFO.MODAL"
      session={session}
      render={(f) => {
        if (f.key === 'internal_code') return (
          <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('cms.pages.drawer.fields.internal_code')}>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.fields.internal_code')}</label>
            <input
              type="text" value={internalCode} disabled={!isCreate}
              onChange={e => setInternalCode(e.target.value.toUpperCase())}
              onBlur={e => { if (isCreate) validateInternalCode(e.target.value); }}
              placeholder={t('cms.pages.drawer.fields.internal_code_placeholder', 'CMS.PAGE.MY_PAGE')}
              className={`h-8 w-full rounded border px-2 font-mono text-csm-sm focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted
                ${internalCodeError ? 'border-csm-danger focus:border-csm-danger' : 'border-csm-border focus:border-primary'}`}
            />
            {internalCodeError && (
              <p className="mt-0.5 text-csm-xs text-csm-danger">{internalCodeError}</p>
            )}
            {!internalCodeError && isCreate && (
              <p className="mt-0.5 text-csm-xs text-csm-muted">{t('cms.pages.drawer.fields.internal_code_hint', 'Uppercase letters, digits, dots, underscores. E.g. CMS.PAGE.MY_PAGE')}</p>
            )}
          </PersonalizedField>
        );
        if (f.key === 'sort_order') return (
          <PersonalizedField componentKey={CK} fieldKey="sort_order" label={t('cms.pages.drawer.fields.sort_order')}>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.fields.sort_order')}</label>
            <input
              type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))}
              className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none"
            />
          </PersonalizedField>
        );
        if (f.key === 'page_type_code') return (
          <PersonalizedField componentKey={CK} fieldKey="page_type_code" label={t('cms.pages.drawer.fields.page_type_code')}>
            <FilteredModalSingleSelect label={t('cms.pages.drawer.fields.page_type_code')} options={typeOptions} selectedCode={pageType}
              onChange={c => { if (c) setPageType(c); }} allowClear={false} />
          </PersonalizedField>
        );
        if (f.key === 'status_code') return (
          <PersonalizedField componentKey={CK} fieldKey="status_code" label={t('cms.pages.drawer.fields.status_code')}>
            <FilteredModalSingleSelect label={t('cms.pages.drawer.fields.status_code')} options={statusOptions} selectedCode={status}
              onChange={c => { if (c) setStatus(c); }} allowClear={false} />
          </PersonalizedField>
        );
        if (f.key === 'parent_page') return (
          <PersonalizedField componentKey={CK} fieldKey="parent_page" label={t('cms.pages.drawer.fields.parent_page')}>
            <FilteredModalSingleSelect
              label={t('cms.pages.drawer.fields.parent_page')}
              options={parentOptions}
              selectedCode={parentId ?? ''}
              onChange={c => setParentId(c || null)}
              allowClear={false}
            />
          </PersonalizedField>
        );
        if (f.key === 'is_active') return (
          <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('cms.pages.drawer.fields.is_active')}>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.fields.is_active')}</label>
            <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-primary" />
              {isActive ? t('common.yes') : t('common.no')}
            </label>
          </PersonalizedField>
        );
        if (udcForm.isUdcKey(f.key)) return (
          <PersonalizedField componentKey={CK} fieldKey={f.key} label={f.label}
            readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
            {udcForm.renderControl(f.key)}
          </PersonalizedField>
        );
        return null;
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Translations tab
// ---------------------------------------------------------------------------

interface TranslationsTabProps {
  translations: CmsPageTlForm[];
  setTranslations: (t: CmsPageTlForm[]) => void;
}

function TranslationsTab({ translations, setTranslations }: TranslationsTabProps) {
  const { t } = useTranslation();
  const { options: languageOptions } = useLanguageOptions();
  const [locale, setLocale] = useState<string>('');
  useEffect(() => {
    if (!locale && languageOptions.length > 0) setLocale(languageOptions[0].code);
  }, [locale, languageOptions]);
  const tl = translations.find(t => t.language_id === locale) ?? emptyTl(locale);

  const update = (patch: Partial<CmsPageTlForm>) => {
    setTranslations(
      translations.some(t => t.language_id === locale)
        ? translations.map(t => t.language_id === locale ? { ...t, ...patch } : t)
        // No row yet for this (possibly newly-active) locale — append one instead of
        // silently dropping the edit; leaves every other row/field untouched.
        : [...translations, { ...emptyTl(locale), ...patch }]
    );
  };

  return (
    <div>
      {/* Locale switcher */}
      <div className="mb-3 flex gap-1">
        {languageOptions.map(o => (
          <button key={o.code} onClick={() => setLocale(o.code)}
                  className={`rounded border px-3 py-0.5 text-csm-xs font-medium transition-colors
                    ${locale === o.code ? 'border-primary bg-primary text-white' : 'border-csm-border bg-white text-csm-ink-soft hover:bg-csm-surface-alt'}`}>
            {o.name}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.translations.page_title', 'Page Title')} <span className="text-csm-danger">*</span></label>
          <input type="text" value={tl.page_title} onChange={e => update({ page_title: e.target.value })}
                 className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
            {t('cms.pages.translations.url_slug', 'URL Slug')} <span className="text-csm-xs text-csm-muted">{t('cms.pages.translations.auto_lowercased', '(auto-lowercased)')}</span>
          </label>
          <input type="text" value={tl.slug} onChange={e => update({ slug: e.target.value })}
                 placeholder={t('cms.pages.translations.slug_placeholder', 'my-page-url')} className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
        </div>
        <div>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.translations.description', 'Description')}</label>
          <textarea rows={3} value={tl.page_description} onChange={e => update({ page_description: e.target.value })}
                    className="w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-primary focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.translations.meta_title', 'Meta Title')}</label>
            <input type="text" value={tl.meta_title} onChange={e => update({ meta_title: e.target.value })}
                   className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.translations.meta_description', 'Meta Description')}</label>
            <input type="text" value={tl.meta_description} onChange={e => update({ meta_description: e.target.value })}
                   className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Elements tab  (content element management)
// ---------------------------------------------------------------------------

interface ElementsTabProps { session: AuthSession; pageId: string; }

function ElementsTab({ session, pageId }: ElementsTabProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const elementNames = useLookupNames('cms_element_type', 'cms_element_type_tl', { codeCol: 'element_type_code', localeCol: 'language_id' });
  const [elements, setElements]     = useState<CmsElementHydrated[]>([]);
  const [elementTypes, setElementTypes] = useState<CmsElementTypeRow[]>([]);
  const [editingEl, setEditingEl]   = useState<CmsElementHydrated | null>(null);
  const [creatingEl, setCreatingEl] = useState(false);
  const [deleteElTarget, setDeleteElTarget] = useState<CmsElementHydrated | null>(null);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const loadElements = useCallback(async () => {
    setBusy(true);
    try {
      const { data, error: e } = await apiDb
        .from('cms_content_element')
        .select('*')
        .eq('page_id', pageId)
        .order('sort_order');
      if (e) throw e;
      setElements((data ?? []) as CmsElementHydrated[]);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [pageId]);

  useEffect(() => {
    void loadElements();
    void apiDb.from('cms_element_type').select('*').order('sort_order')
      .then(({ data }) => setElementTypes((data ?? []) as CmsElementTypeRow[]));
  }, [loadElements]);

  const handleDeleteEl = async (el: CmsElementHydrated) => {
    setBusy(true);
    try {
      const { error: e } = await routedDelete(session, 'cms_content_element', 'element_id', el.element_id);
      if (e) throw e;
      await loadElements();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const moveElement = async (el: CmsElementHydrated, direction: 'up' | 'down') => {
    const idx = elements.findIndex(e => e.element_id === el.element_id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= elements.length) return;
    const swap = elements[swapIdx];
    await routedUpdate(session, 'cms_content_element', 'element_id', el.element_id, { sort_order: swap.sort_order });
    await routedUpdate(session, 'cms_content_element', 'element_id', swap.element_id, { sort_order: el.sort_order });
    await loadElements();
  };

  const elementTypeOptions = elementTypes.map(t => ({ internal_code: t.element_type_code, display_text: t.element_type_code }));

  return (
    <>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.elements.label')} ({elements.length})</span>
          {canAdd && (<button onClick={() => setCreatingEl(true)}
                  className="h-6 rounded border border-primary bg-primary px-2 text-csm-xs text-white hover:bg-primary-hover">
            {t('cms.pages.drawer.elements.add_element')}
          </button>)}
        </div>
        {error && <div className="mb-2 text-csm-xs text-csm-danger">{error}</div>}
        {elements.length === 0 && !busy && (
          <div className="rounded border border-dashed border-csm-border py-6 text-center text-csm-sm italic text-csm-muted">
            {t('cms.pages.drawer.elements.empty')}
          </div>
        )}
        <div className="space-y-1">
          {elements.map((el, idx) => (
            <div key={el.element_id} className="flex items-center gap-2 rounded border border-csm-border bg-csm-surface-alt px-2 py-1.5">
              <span className="w-5 text-center text-csm-xs text-csm-muted">{el.sort_order}</span>
              <span className={`rounded px-1.5 py-0.5 text-csm-2xs font-medium uppercase tracking-wider
                ${el.element_type_code === 'text_block' ? 'bg-csm-info-soft text-csm-info border border-csm-info-border'
                : el.element_type_code === 'image' ? 'bg-csm-success-soft text-csm-success border border-csm-success-border'
                : el.element_type_code === 'video_embed' ? 'bg-csm-warning-soft text-csm-warning border border-csm-warning-border'
                : 'bg-white text-csm-ink-soft border border-csm-border'}`}>
                {elementNames[el.element_type_code] ?? el.element_type_code}
              </span>
              <span className="flex-1 truncate font-mono text-csm-xs text-csm-muted">{el.element_id.slice(0, 8)}…</span>
              <YesNoBadge value={el.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.off', 'Off')} />
              <div className="flex gap-1">
                <button onClick={() => void moveElement(el, 'up')} disabled={idx === 0}
                        className="h-5 w-5 rounded border border-csm-border bg-white text-csm-xs disabled:opacity-30 hover:bg-csm-border-soft">↑</button>
                <button onClick={() => void moveElement(el, 'down')} disabled={idx === elements.length - 1}
                        className="h-5 w-5 rounded border border-csm-border bg-white text-csm-xs disabled:opacity-30 hover:bg-csm-border-soft">↓</button>
                {canChange && (<button onClick={() => setEditingEl(el)}
                        className="rounded border border-csm-border bg-white px-1.5 py-0 text-csm-xs hover:bg-csm-surface-alt">{t('common.edit')}</button>)}
                <button onClick={() => setDeleteElTarget(el)}
                        className="rounded border border-csm-danger-border bg-csm-danger-soft px-1.5 py-0 text-csm-xs text-csm-danger hover:opacity-80">✕</button>
              </div>
            </div>
          ))}
        </div>

        {(editingEl || creatingEl) && (
          <ElementEditModal
            session={session}
            pageId={pageId}
            element={editingEl}
            elementTypeOptions={elementTypeOptions}
            nextSortOrder={elements.length + 1}
            onClose={() => { setEditingEl(null); setCreatingEl(false); }}
            onSaved={() => { setEditingEl(null); setCreatingEl(false); void loadElements(); }}
          />
        )}
      </div>

      {deleteElTarget && (
        <ConfirmModal
          title={t('cms.pages.drawer.elements.delete.title', 'Delete Element?')}
          message={t('cms.pages.drawer.elements.delete.message', 'Delete this {{type}} element?', { type: deleteElTarget.element_type_code })}
          onConfirm={async () => { await handleDeleteEl(deleteElTarget); setDeleteElTarget(null); }}
          onCancel={() => setDeleteElTarget(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Element edit modal (inline modal inside the drawer)
// ---------------------------------------------------------------------------

interface ElementEditModalProps {
  session: AuthSession;
  pageId: string;
  element: CmsContentElementRow | null;
  elementTypeOptions: { internal_code: string; display_text: string }[];
  nextSortOrder: number;
  onClose: () => void;
  onSaved: () => void;
}

function ElementEditModal({ session, pageId, element, elementTypeOptions, nextSortOrder, onClose, onSaved }: ElementEditModalProps) {
  const { t } = useTranslation();
  const isCreate = element === null;
  const [elType, setElType]         = useState(element?.element_type_code ?? 'text_block');
  const [sortOrder, setSortOrder]   = useState(element?.sort_order ?? nextSortOrder);
  const [cssClass, setCssClass]     = useState(element?.css_class_override ?? '');
  const [isActive, setIsActive]     = useState(element ? element.is_active === 'Y' : true);
  // Text content
  const [bodyHtml, setBodyHtml]     = useState('');
  // Image content
  const [imageAssetId, setImageAssetId] = useState('');
  const [linkUrl, setLinkUrl]       = useState('');
  // Video content
  const [providerCode, setProviderCode] = useState('youtube');
  const [videoId, setVideoId]       = useState('');
  // Dataset content (cds)
  const [datasetCode, setDatasetCode] = useState('');
  const [viewMode, setViewMode]     = useState<'TABLE' | 'CARDS' | 'DETAIL'>('TABLE');
  const [datasetOptions, setDatasetOptions] = useState<{ internal_code: string; display_text: string }[]>([]);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [assets, setAssets]         = useState<CmsAssetRow[]>([]);
  const [providers, setProviders]   = useState<{ internal_code: string; display_text: string }[]>([]);

  useEffect(() => {
    void apiDb.from('cms_asset').select('asset_id, original_filename, asset_type_code').eq('is_active', 'Y')
      .then(({ data }) => setAssets((data ?? []) as CmsAssetRow[]));
    void apiDb.from('cms_video_provider').select('provider_code').eq('is_active', 'Y')
      .then(({ data }) => setProviders((data ?? []).map((p: { provider_code: string }) => ({ internal_code: p.provider_code, display_text: p.provider_code }))));
    void apiDb.from('cds_dataset').select('internal_code, name').eq('is_active', 'Y')
      .then(({ data }) => setDatasetOptions((data ?? []).map((d: { internal_code: string; name: string }) => ({ internal_code: d.internal_code, display_text: d.name }))));
    // Load existing sub-type data
    if (element) {
      if (element.element_type_code === 'text_block') {
        void apiDb.from('cms_text_content_tl').select('body_html').eq('element_id', element.element_id).eq('language_id', 'EN_US').single()
          .then(({ data }) => { if (data) setBodyHtml((data as { body_html?: string }).body_html ?? ''); });
      } else if (element.element_type_code === 'image') {
        void apiDb.from('cms_image_content').select('asset_id, link_url').eq('element_id', element.element_id).single()
          .then(({ data }) => { if (data) { const row = data as { asset_id?: string; link_url?: string }; setImageAssetId(row.asset_id ?? ''); setLinkUrl(row.link_url ?? ''); } });
      } else if (element.element_type_code === 'video_embed') {
        void apiDb.from('cms_video_content').select('provider_code, video_id_external').eq('element_id', element.element_id).single()
          .then(({ data }) => { if (data) { const row = data as { provider_code?: string; video_id_external?: string }; setProviderCode(row.provider_code ?? 'youtube'); setVideoId(row.video_id_external ?? ''); } });
      } else if (element.element_type_code === 'dataset') {
        void apiDb.from('cms_dataset_content').select('dataset_code, view_mode').eq('element_id', element.element_id).single()
          .then(({ data }) => { if (data) { const row = data as { dataset_code?: string; view_mode?: 'TABLE' | 'CARDS' | 'DETAIL' }; setDatasetCode(row.dataset_code ?? ''); setViewMode(row.view_mode ?? 'TABLE'); } });
      }
    }
  }, [element]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let elementId = element?.element_id ?? '';
      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'cms_content_element', {
          page_id: pageId, element_type_code: elType,
          sort_order: sortOrder, css_class_override: cssClass || null,
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw e;
        elementId = String(row_id);
        // Insert base sub-type row (1-to-1)
        if (elType === 'text_block') {
          const { error: e2 } = await routedInsert(session, 'cms_text_content', { element_id: elementId });
          // Idempotent: a pre-existing 1-to-1 row is not an error.
          if (e2 && !/already exists|duplicate/i.test(e2.message)) throw e2;
        } else if (elType === 'image') {
          const { error: e2 } = await routedInsert(session, 'cms_image_content', { element_id: elementId, asset_id: imageAssetId });
          if (e2) throw e2;
        } else if (elType === 'video_embed') {
          const { error: e2 } = await routedInsert(session, 'cms_video_content', { element_id: elementId, provider_code: providerCode, video_id_external: videoId });
          if (e2) throw e2;
        } else if (elType === 'dataset') {
          const { error: e2 } = await routedInsert(session, 'cms_dataset_content', { element_id: elementId, dataset_code: datasetCode, view_mode: viewMode });
          if (e2) throw e2;
        }
      } else {
        const { error: e } = await routedUpdate(session, 'cms_content_element', 'element_id', elementId, {
          sort_order: sortOrder, css_class_override: cssClass || null,
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw e;
      }
      // Upsert sub-type content
      if (elType === 'text_block') {
        // cms_text_content_tl has a composite PK (element_id, language_id) and is not
        // registered with the Common Routine (single-PK only, §2) — keep the direct upsert.
        await apiDb.from('cms_text_content_tl').upsert({
          element_id: elementId, language_id: 'EN_US', body_html: bodyHtml, body_plain: null,
        }, { onConflict: 'element_id,language_id' });
      } else if (elType === 'image' && !isCreate) {
        await routedUpdate(session, 'cms_image_content', 'element_id', elementId, { asset_id: imageAssetId, link_url: linkUrl || null });
      } else if (elType === 'video_embed' && !isCreate) {
        await routedUpdate(session, 'cms_video_content', 'element_id', elementId, { provider_code: providerCode, video_id_external: videoId });
      } else if (elType === 'dataset' && !isCreate) {
        await routedUpdate(session, 'cms_dataset_content', 'element_id', elementId, { dataset_code: datasetCode, view_mode: viewMode });
      }
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const assetOptions = assets.map(a => ({ internal_code: a.asset_id, display_text: a.original_filename }));

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="w-[32rem] rounded border border-csm-border bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <header className="flex h-9 items-center justify-between border-b border-csm-border bg-csm-surface-alt px-3">
          <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">
            {isCreate ? t('cms.pages.drawer.elements.add_element') : t('common.edit')}
          </span>
          <button onClick={onClose} className="text-csm-muted hover:text-csm-ink">✕</button>
        </header>
        <div className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-3">
            <FilteredModalSingleSelect label={t('cms.pages.drawer.elements.fields.element_type')} options={elementTypeOptions}
              selectedCode={elType} onChange={c => { if (c) setElType(c); }} allowClear={false} />
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.elements.fields.sort_order', 'Sort Order')}</label>
              <input type="number" value={sortOrder} onChange={e => setSortOrder(Number(e.target.value))}
                     className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.elements.fields.css_class_override', 'CSS Class Override')}</label>
            <input type="text" value={cssClass} onChange={e => setCssClass(e.target.value)} placeholder={t('cms.pages.drawer.elements.fields.css_class_placeholder', 'optional Tailwind class(es)')}
                   className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
          </div>
          {elType === 'text_block' && (
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.elements.fields.body_html', 'Body HTML (EN_US)')}</label>
              <RichTextEditor value={bodyHtml} onChange={setBodyHtml} height={260} />
            </div>
          )}
          {elType === 'image' && (
            <div className="space-y-2">
              <FilteredModalSingleSelect label={t('cms.pages.drawer.elements.fields.asset', 'Asset')} options={assetOptions} selectedCode={imageAssetId}
                onChange={c => { if (c) setImageAssetId(c); }} allowClear={false} />
              <div>
                <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.elements.fields.link_url', 'Link URL (optional)')}</label>
                <input type="text" value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
                       className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none" />
              </div>
            </div>
          )}
          {elType === 'video_embed' && (
            <div className="space-y-2">
              <FilteredModalSingleSelect label={t('cms.pages.drawer.elements.fields.provider', 'Provider')} options={providers} selectedCode={providerCode}
                onChange={c => { if (c) setProviderCode(c); }} allowClear={false} />
              <div>
                <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cms.pages.drawer.elements.fields.video_id', 'Video ID')}</label>
                <input type="text" value={videoId} onChange={e => setVideoId(e.target.value)} placeholder={t('cms.pages.drawer.elements.fields.video_id_placeholder', 'e.g. dQw4w9WgXcQ')}
                       className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-primary focus:outline-none" />
              </div>
            </div>
          )}
          {elType === 'dataset' && (
            <div className="space-y-2">
              <FilteredModalSingleSelect label={t('cds.element.dataset', 'Dataset')} options={datasetOptions} selectedCode={datasetCode}
                onChange={c => { if (c) setDatasetCode(c); }} allowClear={false} />
              <div>
                <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('cds.element.view_mode', 'View mode')}</label>
                <select value={viewMode} onChange={e => setViewMode(e.target.value as 'TABLE' | 'CARDS' | 'DETAIL')}
                        className="h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none">
                  <option value="TABLE">{t('cds.element.view_table', 'Table')}</option>
                  <option value="CARDS">{t('cds.element.view_cards', 'Cards')}</option>
                  <option value="DETAIL">{t('cds.element.view_detail', 'Detail')}</option>
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="flex h-7 items-center gap-2 text-csm-sm text-csm-ink-soft">
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-primary" />
              {t('cms.pages.drawer.elements.fields.is_active', 'Active')}
            </label>
          </div>
          {error && <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-xs text-csm-danger">{error}</div>}
        </div>
        <footer className="flex justify-end gap-2 border-t border-csm-border bg-csm-surface-alt px-3 py-2">
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel')}</button>
          <button onClick={() => void save()} disabled={busy}
                  className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
            {busy ? t('common.saving') : t('common.save')}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security tab  (page ↔ role access mapping)
// ---------------------------------------------------------------------------

interface SecurityTabProps { session: AuthSession; pageId: string; }

function SecurityTab({ session, pageId }: SecurityTabProps) {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const accessNames = useLookupNames('cms_access_level', 'cms_access_level_tl', { codeCol: 'access_level_code', localeCol: 'language_id' });
  const roleNames = useLookupNames('csm_role_master', 'csm_role_master_tl');
  const [rows, setRows]           = useState<CmsPageRoleAccessRow[]>([]);
  const [accessLevels, setAccessLevels] = useState<CmsAccessLevelRow[]>([]);
  const [allRoles, setAllRoles]   = useState<{ role_id: number; display_text: string }[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState('view');
  const [revokeTarget, setRevokeTarget] = useState<CmsPageRoleAccessRow | null>(null);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data, error: e } = await apiDb
        .from('cms_page_role_access')
        .select(`page_id, role_id, access_level_code, created_at, modified_by,
                 csm_role_master!left(role_internal_code)`)
        .eq('page_id', pageId);
      if (e) throw e;
      type RoleAccessJoin = CmsPageRoleAccessRow & { csm_role_master?: { role_internal_code?: string } };
      const flat = ((data ?? []) as RoleAccessJoin[]).map(r => ({
        ...r,
        role_display_text: r.csm_role_master?.role_internal_code ?? String(r.role_id),
      }));
      setRows(flat as CmsPageRoleAccessRow[]);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [pageId]);

  useEffect(() => {
    void load();
    void apiDb.from('cms_access_level').select('access_level_code, sort_order').order('sort_order')
      .then(({ data }) => setAccessLevels((data ?? []) as CmsAccessLevelRow[]));
    void apiDb.from('csm_role_master').select('role_id, role_internal_code').eq('is_active', 'Y')
      .then(({ data }) => setAllRoles(((data ?? []) as { role_id: number; role_internal_code: string }[]).map(r => ({ role_id: r.role_id, display_text: r.role_internal_code }))));
  }, [load]);

  const grantRole = async () => {
    if (!selectedRole) return;
    setBusy(true); setError(null);
    // cms_page_role_access has a composite PK (page_id, role_id) and is not registered
    // with the Common Routine (single-PK only, §2) — keep the direct upsert/delete.
    const actor = String(getForensicActorId(session));
    try {
      const { error: e } = await apiDb.from('cms_page_role_access').upsert({
        page_id: pageId, role_id: Number(selectedRole),
        access_level_code: selectedLevel, modified_by: actor,
      }, { onConflict: 'page_id,role_id' });
      if (e) throw e;
      setSelectedRole('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const revokeRole = async (r: CmsPageRoleAccessRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await apiDb.from('cms_page_role_access')
        .delete().eq('page_id', r.page_id).eq('role_id', r.role_id);
      if (e) throw e;
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const roleOptions = allRoles.map(r => ({ internal_code: String(r.role_id), display_text: r.display_text }));
  const levelOptions = accessLevels.map(l => ({ internal_code: l.access_level_code, display_text: l.access_level_code }));

  return (
    <>
      <div>
        <p className="mb-3 text-csm-xs text-csm-muted">
          {t('cms.pages.drawer.security.hint_prefix', 'A page with no role mappings is private to all roles. Assign at least the')}&nbsp;
          <strong>{t('cms.pages.drawer.security.hint_view', 'view')}</strong> {t('cms.pages.drawer.security.hint_suffix', 'level to make it accessible.')}
        </p>
        {/* Grant row */}
        <div className="mb-3 flex items-end gap-2">
          <div className="flex-1">
            <FilteredModalSingleSelect label={t('cms.pages.drawer.security.columns.role')} options={roleOptions} selectedCode={selectedRole}
              onChange={c => setSelectedRole(c ?? '')} allowClear={false} />
          </div>
          <div className="w-36">
            <FilteredModalSingleSelect label={t('cms.pages.drawer.security.columns.access_level')} options={levelOptions} selectedCode={selectedLevel}
              onChange={c => { if (c) setSelectedLevel(c); }} allowClear={false} />
          </div>
          <button onClick={() => void grantRole()} disabled={!selectedRole || busy}
                  className="h-8 rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-50 mb-0.5">
            {t('cms.pages.drawer.security.grant_role')}
          </button>
        </div>
        {error && <div className="mb-2 text-csm-xs text-csm-danger">{error}</div>}
        {/* Existing grants */}
        <table className="w-full border-collapse text-csm-sm">
          <thead>
            <tr className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <th className="border-b border-csm-border px-2 py-1.5 text-left">{t('cms.pages.drawer.security.columns.role')}</th>
              <th className="border-b border-csm-border px-2 py-1.5 text-left">{t('cms.pages.drawer.security.columns.access_level')}</th>
              <th className="border-b border-csm-border px-2 py-1.5 text-left">{t('cms.pages.drawer.security.columns.granted_at')}</th>
              <th className="border-b border-csm-border px-2 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.role_id} className="hover:bg-csm-surface-alt">
                <td className="border-b border-csm-border-soft px-2 py-1.5 text-csm-xs">{(r.role_display_text && (roleNames[r.role_display_text] ?? r.role_display_text)) ?? '—'}</td>
                <td className="border-b border-csm-border-soft px-2 py-1.5">
                  <span className="rounded bg-primary-soft px-1.5 py-0.5 text-csm-xs font-medium text-primary">
                    {accessNames[r.access_level_code] ?? r.access_level_code}
                  </span>
                </td>
                <td className="border-b border-csm-border-soft px-2 py-1.5 text-csm-ink-soft">
                  {formatDate(r.created_at)}
                </td>
                <td className="border-b border-csm-border-soft px-2 py-1.5 text-right">
                  <button onClick={() => setRevokeTarget(r)}
                          className="rounded border border-csm-danger-border bg-csm-danger-soft px-1.5 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">
                    {t('cms.pages.drawer.security.revoke')}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr><td colSpan={4} className="px-2 py-4 text-center italic text-csm-muted">{t('cms.pages.drawer.security.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {revokeTarget && (
        <ConfirmModal
          title={t('cms.pages.drawer.security.revoke_confirm.title', 'Revoke Access?')}
          message={t('cms.pages.drawer.security.revoke_confirm.message', 'Revoke {{role}} access from this page?', { role: revokeTarget.role_display_text ?? '—' })}
          confirmLabel={t('cms.pages.drawer.security.revoke', 'Revoke')}
          onConfirm={async () => { await revokeRole(revokeTarget); setRevokeTarget(null); }}
          onCancel={() => setRevokeTarget(null)}
        />
      )}
    </>
  );
}
